import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Eyebrow } from "./Reveal";
import { Smartphone, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TryIt() {
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/try-it`, { phone });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong — try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="try-it-section" className="relative border-t border-white/[0.06] bg-[#0E1117] py-24 lg:py-28">
      <div className="absolute left-1/2 top-1/2 h-[300px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#F25F22]/[0.07] blur-[120px]" />
      <div className="relative mx-auto max-w-2xl px-5 text-center sm:px-8">
        <Reveal>
          <Eyebrow>Try It Right Now</Eyebrow>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            Feel what your callers feel
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-base text-slate-400">
            Type your mobile number. Backfill will treat it like a missed call
            and text you the exact message your customers would get — in
            seconds.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="mx-auto mt-8 flex max-w-md gap-2.5" data-testid="try-it-form">
            <div className="relative flex-1">
              <Smartphone className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                type="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="try-it-phone-input"
                placeholder="+1 555 014 2231"
                className="w-full rounded-md border border-white/[0.1] bg-[#0B0D10] py-3.5 pl-10 pr-4 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors focus:border-[#F25F22]/60 focus:ring-2 focus:ring-[#F25F22]/20"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="try-it-submit"
              className="flex items-center gap-2 rounded-md bg-[#F25F22] px-5 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#FF7539] hover:shadow-[0_0_24px_rgba(242,95,34,0.45)] disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Text Me
            </button>
          </form>

          {error && (
            <p data-testid="try-it-error" className="mt-4 text-sm text-red-400">{error}</p>
          )}

          <AnimatePresence>
            {result && (
              <motion.div
                initial={{ opacity: 0, y: 16, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                data-testid="try-it-result"
                className="mx-auto mt-8 max-w-md rounded-xl border border-white/[0.1] bg-[#12161F] p-6 text-left"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
                  {result.simulated ? "Simulation — engine responded" : "Sent — check your phone"}
                </p>
                <div className="mt-4 max-w-[90%] rounded-2xl rounded-bl-sm border border-white/[0.08] bg-[#1A212E] px-4 py-3 text-sm leading-relaxed text-slate-200">
                  {result.preview}
                </div>
                <p className="mt-4 text-xs leading-relaxed text-slate-500">
                  {result.simulated
                    ? "Real texting activates the moment phone lines connect — this is the exact message, branding included."
                    : "That just happened in seconds. Imagine every missed call going this way."}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </Reveal>
      </div>
    </section>
  );
}
